import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, MailCheck } from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SeoHead, SEO_PRESETS } from "@/components/SeoHead";
import api, { formatApiError } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative bg-background overflow-x-hidden">
      <SeoHead {...SEO_PRESETS.forgotPassword} />
      <div className="pointer-events-none absolute inset-0 -z-0">
        <div className="glow-orb glow-orb-teal w-[500px] h-[500px] -top-32 -right-32" />
        <div className="glow-orb glow-orb-navy w-[400px] h-[400px] -bottom-32 -left-32" />
      </div>

      <header className="relative z-10">
        <div className="container flex h-16 sm:h-20 items-center justify-between">
          <Logo size="md" />
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button asChild variant="ghost">
              <Link to="/login"><ArrowLeft className="h-4 w-4" /> Sign In</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 px-4 py-12 flex items-center justify-center">
        <Card className="glass-card w-full max-w-md">
          <CardContent className="p-7 sm:p-10">
            {sent ? (
              <div className="text-center" data-testid="forgot-success">
                <div className="mx-auto h-14 w-14 rounded-2xl bg-teal/15 flex items-center justify-center mb-4">
                  <MailCheck className="h-7 w-7 text-teal" />
                </div>
                <h1 className="text-xl font-heading font-bold">Link Sent</h1>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  If your email is registered, a password reset link will be sent.
                </p>
                <Button asChild className="mt-6 w-full">
                  <Link to="/login">Back to Sign In</Link>
                </Button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <h1 className="text-2xl font-heading font-bold tracking-tight">Reset Your Password</h1>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Portal eLearningSaham · by Maahad Bursa</p>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                    Enter your registered email address. We will send you a password reset link.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-form">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="forgot-email"
                    />
                  </div>
                  {error && <p className="text-sm text-destructive">{error}</p>}
                  <Button type="submit" className="w-full btn-glow" size="lg" disabled={submitting} data-testid="forgot-submit">
                    {submitting ? "Sending..." : "Send Reset Link"}
                  </Button>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
